import React, { useEffect, useRef, useState } from "react";
import {
  ChevronRight, Check, ArrowLeft, Zap, Tv2,
  Calendar, Loader2, CreditCard, Receipt, Wifi,
  AlertTriangle, Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import SearchableSelect from "@/components/ui/SearchableSelect";

/* ─── Helpers ──────────────────────────────────────────────────────────── */
const fmtMrp = (plan) => `₹${Number(plan?.base_cost || plan?.sell_price || 0).toFixed(2)}`;

const STEPS = ["DTH ID", "Plan", "Pay"];

const POLL_INTERVAL_MS = 3000;   // poll every 3s
const POLL_TIMEOUT_MS = 120000; // give up after 2 min

/* ─── StepBar ───────────────────────────────────────────────────────────── */
function StepBar({ step }) {
  return (
    <div className="flex items-center justify-center gap-1 mb-8">
      {STEPS.map((label, i) => (
        <React.Fragment key={label}>
          <div className="flex flex-col items-center">
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all ${i < step
                  ? "bg-green-500 text-white shadow-sm"
                  : i === step
                    ? "bg-brand-primary text-white shadow-md ring-4 ring-brand-primary/20"
                    : "bg-slate-100 text-slate-400"
                }`}
            >
              {i < step ? <Check className="w-4 h-4" /> : i + 1}
            </div>
            <span
              className={`text-[10px] mt-1 font-semibold ${i === step ? "text-brand-primary" : "text-slate-400"
                }`}
            >
              {label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div
              className={`h-0.5 w-12 mb-4 rounded ${i < step ? "bg-green-400" : "bg-slate-200"
                }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ─── Plan Card (customer view — MRP only) ─────────────────────────────── */
function PlanCard({ p, selected, onSelect }) {
  const isSelected = selected?.id === p.id;
  return (
    <button
      onClick={() => onSelect(p)}
      className={`w-full text-left rounded-xl border-2 p-4 transition-all ${isSelected
          ? "border-brand-primary bg-brand-primary/5 shadow-md"
          : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
        }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold text-brand-primary uppercase tracking-wide">{p.operator}</span>
            <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-medium">{p.category}</span>
          </div>
          <p className="font-semibold text-brand-nav truncate">{p.name}</p>
          {p.description && (
            <p className="text-xs text-slate-400 mt-1 line-clamp-1">{p.description}</p>
          )}
          <div className="flex gap-3 mt-2 flex-wrap">
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {p.validity_days} days
            </span>
            {p.channel_count > 0 && (
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <Tv2 className="w-3 h-3" /> {p.channel_count} channels
              </span>
            )}
          </div>
        </div>
        {/* Customer ONLY sees MRP (base_cost) */}
        <div className="text-right shrink-0">
          <p className="font-heading font-black text-2xl text-brand-accent">{fmtMrp(p)}</p>
          <p className="text-xs text-slate-400">{p.validity_days}d</p>
        </div>
      </div>
    </button>
  );
}

/* ─── Razorpay Script Loader ──────────────────────────────────────────── */
function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/* ─── Processing Screen (shows while polling) ──────────────────────────── */
function ProcessingScreen({ rechargeId, onDone }) {
  const [dots, setDots] = useState(".");
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const timerRef = useRef(null);
  const startRef = useRef(Date.now());

  useEffect(() => {
    // Animated dots
    const dotInterval = setInterval(() => {
      setDots(d => d.length >= 3 ? "." : d + ".");
    }, 500);

    // Elapsed seconds counter
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);

    // Poll for status
    const poll = async () => {
      if (Date.now() - startRef.current > POLL_TIMEOUT_MS) {
        clearInterval(pollRef.current);
        clearInterval(timerRef.current);
        clearInterval(dotInterval);
        onDone({ status: "recharge_failed", ok: false, message: "Recharge timed out. Please contact support." });
        return;
      }
      try {
        const { data } = await api.get(`/customer/recharge/${rechargeId}/status`);
        if (data.status === "success" || data.status === "recharge_failed" || data.status === "failed") {
          clearInterval(pollRef.current);
          clearInterval(timerRef.current);
          clearInterval(dotInterval);
          onDone(data);
        }
        // else still processing — keep polling
      } catch (_) {
        // Network error — keep polling silently
      }
    };

    // First poll after 2s (webhook usually fires within 1-2s)
    setTimeout(poll, 2000);
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      clearInterval(pollRef.current);
      clearInterval(timerRef.current);
      clearInterval(dotInterval);
    };
  }, [rechargeId, onDone]);

  return (
    <div className="text-center py-12 space-y-6">
      {/* Animated spinner ring */}
      <div className="relative w-28 h-28 mx-auto">
        <div className="absolute inset-0 rounded-full border-4 border-brand-primary/20" />
        <div className="absolute inset-0 rounded-full border-4 border-t-brand-primary animate-spin" />
        <div className="absolute inset-0 flex items-center justify-center">
          <Zap className="w-10 h-10 text-brand-primary" />
        </div>
      </div>

      <div>
        <h2 className="font-heading font-bold text-2xl text-brand-nav">
          Processing Recharge{dots}
        </h2>
        <p className="text-slate-500 mt-2 text-sm max-w-xs mx-auto">
          Payment received! We're activating your DTH subscription. This usually takes a few seconds.
        </p>
      </div>

      {/* Progress bar */}
      <div className="max-w-xs mx-auto">
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-brand-primary to-brand-accent rounded-full transition-all duration-1000"
            style={{ width: `${Math.min(95, (elapsed / (POLL_TIMEOUT_MS / 1000)) * 100 + 5)}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-slate-400 mt-1">
          <span>Payment verified</span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" /> {elapsed}s
          </span>
        </div>
      </div>

      <p className="text-xs text-slate-400">
        Please don't close this page. You'll be redirected automatically.
      </p>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────────── */
export default function Recharge() {
  const [step, setStep] = useState(0);
  const [ids, setIds] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [result, setResult] = useState(null);
  const [rechargeId, setRechargeId] = useState(null); // for polling
  const [feeInfo, setFeeInfo] = useState({ gateway_fee_percent: 0, gateway_fee_bearer: 'user' });

  const [selectedConnection, setSelectedConnection] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planSearch, setPlanSearch] = useState("");
  const [planCategory, setPlanCategory] = useState("All");

  useEffect(() => {
    Promise.all([
      api.get("/customer/ids"),
      api.get("/customer/catalog"),
      api.get("/customer/gateway-fee-info"),
    ])
      .then(([r1, r2, r3]) => { setIds(r1.data); setCatalog(r2.data); setFeeInfo(r3.data); })
      .catch(() => toast.error("Failed to load data"))
      .finally(() => setLoading(false));
  }, []);

  const go = (n) => setStep(n);
  const back = () => setStep(s => s - 1);
  const reset = () => {
    setStep(0); setSelectedConnection(null); setSelectedPlan(null);
    setResult(null); setRechargeId(null);
  };

  /* ── Payment handler ────────────────────────────────── */
  const doRecharge = async () => {
    if (!selectedConnection || !selectedPlan) return;
    setPaying(true);
    try {
      // 1. Create Razorpay order
      const { data } = await api.post("/customer/recharge", {
        customer_id_fk: selectedConnection.id,
        product_id: selectedPlan.id,
        gateway: "razorpay",
      });

      if (!data.ok || data.status !== "pending") {
        toast.error("Failed to create payment order");
        setPaying(false);
        return;
      }

      // 2. Load Razorpay script
      const loaded = await loadRazorpay();
      if (!loaded) {
        toast.error("Payment gateway failed to load. Check your internet connection.");
        setPaying(false);
        return;
      }

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: "DTH Recharge",
        description: `${selectedPlan.name} - ${selectedPlan.validity_days} days` +
          (data.gateway_fee > 0 ? ` (incl. ${data.gateway_fee_percent}% gateway fee)` : ''),

        handler: async (response) => {
          // 3. Verify signature only — DishTV is triggered by webhook
          try {
            const verifyRes = await api.post("/customer/recharge/verify", {
              recharge_id: data.recharge_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (verifyRes.data.ok) {
              // 4. Show processing screen and start polling
              setRechargeId(data.recharge_id);
              go(3); // processing screen
              toast.success("Payment confirmed! Activating your DTH...");
            } else if (verifyRes.data.status === 'recharge_failed') {
              setRechargeId(data.recharge_id);
              go(3); // go to polling screen to show failure details
              toast.error(verifyRes.data.message || "Recharge failed. Our team will review.");
            } else {
              toast.error(verifyRes.data.message || "Payment verification failed. Please contact support.");
            }
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Verification failed");
          } finally {
            setPaying(false);
          }
        },

        modal: {
          ondismiss: () => {
            setPaying(false);
            toast.info("Payment cancelled");
          },
        },
        theme: { color: "#6366f1" },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        setPaying(false);
        toast.error(resp.error?.description || "Payment failed");
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recharge failed");
      setPaying(false);
    }
  };

  /* ── Polling done callback ──────────────────────────── */
  const handlePollDone = (data) => {
    setResult(data);
    go(4); // final result screen
    if (data.status === "success") {
      toast.success("Recharge successful! Your subscription is now active.");
    } else {
      toast.error("Recharge failed. Please contact support.");
    }
  };

  const categories = ["All", ...new Set(catalog.map(p => p.category))];
  const filteredCatalog = catalog.filter(p => {
    const q = planSearch.toLowerCase();
    return (
      (planCategory === "All" || p.category === planCategory) &&
      (!q || p.name.toLowerCase().includes(q) || p.operator.toLowerCase().includes(q))
    );
  });

  if (loading)
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
      </div>
    );

  return (
    <div className="pt-4 max-w-lg mx-auto">
      <div className="mb-6">
        <h3 className="font-heading font-bold text-2xl text-brand-nav">Recharge</h3>
        <p className="text-sm text-slate-400 mt-0.5">Renew your DTH subscription in a few steps.</p>
      </div>

      {/* Show step bar only for steps 0-2 */}
      {step <= 2 && <StepBar step={step} />}

      {/* ── Step 0: Select DTH Connection ── */}
      {step === 0 && (
        <div>
          <h4 className="font-semibold text-brand-nav mb-3">Select Your DTH Connection</h4>
          {ids.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Wifi className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No active connections found.</p>
              <p className="text-sm mt-1">Contact your operator to add a DTH ID.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {ids.map((c) => (
                <button
                  key={c.id}
                  onClick={() => { setSelectedConnection(c); go(1); }}
                  className="w-full text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-brand-primary hover:shadow-md transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-brand-nav text-lg">{c.operator}</p>
                      <p className="text-xs font-mono text-slate-400 mt-0.5">ID: {c.dth_id}</p>
                      {c.subscription && (
                        <p className="text-xs text-green-600 font-medium mt-1">
                          Active until {c.subscription.plan_end}
                        </p>
                      )}
                      {!c.subscription && (
                        <p className="text-xs text-amber-500 font-medium mt-1">No active subscription</p>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Step 1: Choose Plan ── */}
      {step === 1 && selectedConnection && (
        <div>
          <button onClick={back} className="flex items-center gap-1 text-sm text-brand-primary font-semibold mb-4">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 mb-5 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-400">Recharging for</p>
              <p className="font-semibold text-brand-nav">{selectedConnection.operator}</p>
              <p className="text-xs font-mono text-slate-400">{selectedConnection.dth_id}</p>
            </div>
            <Zap className="w-5 h-5 text-brand-primary opacity-60" />
          </div>
          <div className="flex gap-2 mb-3 flex-wrap">
            <input type="text" placeholder="Search plans..." value={planSearch}
              onChange={e => setPlanSearch(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none flex-1 min-w-0" />
            <div className="w-40">
              <SearchableSelect value={planCategory} onChange={setPlanCategory} options={categories.map(c => ({ label: c, value: c }))} />
            </div>
          </div>
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {filteredCatalog.map(p => (
              <PlanCard key={p.id} p={p} selected={selectedPlan}
                onSelect={plan => { setSelectedPlan(plan); go(2); }} />
            ))}
            {filteredCatalog.length === 0 && (
              <p className="text-slate-400 text-sm text-center py-8">No plans available.</p>
            )}
          </div>
        </div>
      )}

      {/* ── Step 2: Confirm & Pay ── */}
      {step === 2 && selectedConnection && selectedPlan && (() => {
        const planAmt = Number(selectedPlan?.base_cost || selectedPlan?.sell_price || 0);
        const showFee = feeInfo.gateway_fee_bearer === 'user' && feeInfo.gateway_fee_percent > 0;
        const feeAmt = showFee ? Math.round(planAmt * feeInfo.gateway_fee_percent) / 100 : 0;
        const grandTotal = Math.round((planAmt + feeAmt) * 100) / 100;
        const fmtAmt = (v) => `₹${v.toFixed(2)}`;
        return (
          <div>
            <button onClick={back} className="flex items-center gap-1 text-sm text-brand-primary font-semibold mb-4">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <h4 className="font-semibold text-brand-nav mb-4">Confirm & Pay</h4>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-5 shadow-sm">
              <div className="px-5 py-4 bg-slate-50 border-b border-slate-100">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">DTH Connection</p>
                <p className="font-semibold text-brand-nav">{selectedConnection.operator}</p>
                <p className="text-xs font-mono text-slate-400">{selectedConnection.dth_id}</p>
              </div>
              <div className="px-5 py-4 border-b border-slate-100 space-y-2 text-sm">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Plan Details</p>
                <div className="flex justify-between">
                  <span className="text-slate-500">Plan</span>
                  <span className="font-semibold">{selectedPlan.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Validity</span>
                  <span>{selectedPlan.validity_days} days</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Operator</span>
                  <span>{selectedPlan.operator}</span>
                </div>
              </div>

              {/* Plan amount */}
              <div className="px-5 py-4 border-b border-slate-100">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Plan Amount</span>
                  <span className="font-heading font-bold text-xl text-slate-700">{fmtAmt(planAmt)}</span>
                </div>
              </div>

              {/* Gateway Fee line — only if user bears the fee */}
              {showFee && (
                <div className="px-5 py-3 border-b border-dashed border-amber-200 bg-amber-50/50">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs font-bold text-amber-600 uppercase tracking-wider">Transaction Fee ({feeInfo.gateway_fee_percent}%)</p>
                      <p className="text-[11px] text-amber-500 mt-0.5">Payment gateway processing charge</p>
                    </div>
                    <p className="font-heading font-bold text-lg text-amber-600">+ {fmtAmt(feeAmt)}</p>
                  </div>
                </div>
              )}

              {/* Grand Total */}
              <div className="px-5 py-5 flex items-center justify-between"
                style={{ background: "linear-gradient(135deg,#6366f1 0%,#8b5cf6 100%)" }}>
                <div>
                  <p className="text-xs font-bold text-indigo-200 uppercase tracking-widest">Total to Pay</p>
                  <p className="text-xs text-indigo-300 mt-0.5">Secure payment via Razorpay</p>
                </div>
                <p className="font-heading font-black text-4xl text-white">{fmtAmt(grandTotal)}</p>
              </div>
            </div>

            <button onClick={doRecharge} disabled={paying}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-brand-primary to-brand-accent text-white font-bold py-4 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-60">
              {paying ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Opening payment…</>
              ) : (
                <><CreditCard className="w-5 h-5" /> Pay {fmtAmt(grandTotal)}</>
              )}
            </button>
            <p className="text-center text-xs text-slate-400 mt-2">
              Secure payment via Razorpay. Your DTH will be activated automatically.
            </p>
          </div>
        );
      })()}

      {/* ── Step 3: Processing (polling) ── */}
      {step === 3 && rechargeId && (
        <ProcessingScreen rechargeId={rechargeId} onDone={handlePollDone} />
      )}

      {/* ── Step 4: Final Result ── */}
      {step === 4 && result && (
        <div className="text-center space-y-5 py-8">
          {result.status === "success" ? (
            <>
              <div className="w-24 h-24 mx-auto rounded-full bg-green-50 border-4 border-green-100 flex items-center justify-center">
                <Check className="w-12 h-12 text-green-500" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-2xl text-brand-nav">Recharge Successful!</h2>
                <p className="text-slate-500 mt-1">Your DTH subscription is now active.</p>
              </div>
              {result?.plan_end && (
                <div className="bg-green-50 border border-green-100 rounded-xl px-6 py-4 inline-block mx-auto">
                  <p className="text-sm text-green-700">Active until</p>
                  <p className="font-heading font-bold text-xl text-green-800">{result.plan_end}</p>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="w-24 h-24 mx-auto rounded-full bg-amber-50 border-4 border-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-12 h-12 text-amber-500" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-2xl text-brand-nav">Payment Received</h2>
                <p className="text-slate-500 mt-1">Your recharge is being reviewed by our team.</p>
                <p className="text-sm text-amber-600 mt-2 bg-amber-50 rounded-lg px-4 py-2 inline-block">
                  {result.message || "We'll process your recharge shortly."}
                </p>
              </div>
            </>
          )}
          <div className="space-y-2">
            <button onClick={reset} className="block mx-auto text-sm font-semibold text-brand-primary hover:underline">
              <Receipt className="w-4 h-4 inline mr-1" />
              Recharge another connection
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
